import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';

import {
  goNext,
  goBack,
  startNewSubmission
} from '../../store/slices/ideaSubmissionSlice';
import Snowfall from 'react-snowfall';
import { Stepper } from './Stepper/Stepper';
import { ChooseThemeStep } from './ChooseThemeStep/ChooseThemeStep';
import { IdeaQuestionsStep } from './IdeaQuestionsStep/IdeaQuestionsStep';
import { SubmitDetailsStep } from './SubmitDetailsStep/SubmitDetailsStep';
import { ReviewSubmitStep } from './ReviewSubmitStep/ReviewSubmitStep';
import { SubmissionCompleted } from './SubmissionCompleted/SubmissionCompleted';
import backgroundImg from '../../../assets/lottie/background.png';
import './IdeaSubmissionApp.css';

export default function IdeaSubmissionApp({ data }) {
  const dispatch = useDispatch();

  const currentStep = useSelector(
    (state) => state.ideaSubmission.currentStep
  );

  const { submitSuccess } = useSelector(
    (state) => state.ideaSubmission
  );

  const { isLoading, hasSubmitted, error, submissionData } = useSelector(
    (state) => state.ideaSubmission.auth
  );

  /* ================= LOADING ================= */
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <p style={{ color: '#666', fontSize: '1.1rem' }}>
          Initializing...
        </p>
      </Box>
    );
  }

  /* ================= ERROR ================= */
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
        padding={3}
      >
        <div style={{ fontSize: '4rem' }}>⚠️</div>
        <h2 style={{ color: '#e53e3e' }}>Authentication Error</h2>
        <p style={{ color: '#666', textAlign: 'center', maxWidth: '500px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            borderRadius: '8px',
            border: 'none',
            background: '#667eea',
            color: 'white',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Retry
        </button>
      </Box>
    );
  }

  /* ================= ALREADY SUBMITTED (HISTORICAL) ================= */
  if (hasSubmitted) {
    return (
      <SubmissionCompleted
        mode="already_submitted"
        submissionData={submissionData}
        isLoading={isLoading}
        onNewSubmission={() => dispatch(startNewSubmission())}
      />
    );
  }

  /* ================= JUST SUBMITTED (CURRENT SESSION) ================= */
  if (submitSuccess) {
    return (
      <SubmissionCompleted
        mode="submission_successful"
        submissionData={submissionData}
        isLoading={isLoading}
        onNewSubmission={() => dispatch(startNewSubmission())}
      />
    );
  }

  /* ================= ACTIVE FORM FLOW ================= */
return (
  <div className="idea-submission-wrapper">
    {/* ❄️ SNOW BACKGROUND */}
   <Snowfall
  snowflakeCount={120}
   color="#adcef7ff"
  style={{
  
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    zIndex: 1,            // ⬅️ IMPORTANT
    pointerEvents: 'none',
  }}
/>

    {/* 🏔️ BOTTOM WINTER ILLUSTRATION (40% HEIGHT, FIXED) */}
    <div
      className="winter-background"
      style={{ backgroundImage: `url(${backgroundImg})` }}
    />

    {/* ================= APP CONTENT ================= */}
    <div className="idea-submission-app">
      {/* Stepper */}
      <div className="steps-container">
        <Stepper steps={data.steps} />
      </div>

      {/* STEP 1 */}
      {currentStep === 0 && (
        <ChooseThemeStep
          themes={data.themes}
          labels={data.labels}
          onNext={() => dispatch(goNext())}
        />
      )}

      {/* STEP 2 */}
      {currentStep === 1 && (
        <IdeaQuestionsStep
          questions={data.ideaQuestions}
          onNext={() => dispatch(goNext())}
          onBack={() => dispatch(goBack())}
        />
      )}

      {/* STEP 3 */}
      {currentStep === 2 && (
        <SubmitDetailsStep
          submitDetailsConfig={data.submitDetails}
          onNext={() => dispatch(goNext())}
          onBack={() => dispatch(goBack())}
        />
      )}

      {/* STEP 4 */}
      {currentStep === 3 && (
        <ReviewSubmitStep
          onBack={() => dispatch(goBack())}
        />
      )}
    </div>
  </div>
);


}
